<script lang="ts">
  // One-time beta-only welcome gift: a big DP grant and/or a Shards grant (each tracked by its own
  // server flag, so an existing tester who already claimed the DP still gets the Shards once — see
  // rewards.ts claimBetaWelcome / claimBetaShards). Either amount is 0 when already claimed. Doubles
  // as the beta disclosure: it's play money, all data is wiped at official launch, and beta testers
  // get exclusive rewards for taking part. Shown ahead of the daily reward / starter picker.
  import { fade, scale, fly } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import DataWindow from "./ui/DataWindow.svelte";
  import { createHotkey, onEscape } from "../hotkeys.ts";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  let { dp, shards, onClose }: { dp: number; shards: number; onClose: () => void } = $props();
  const gifts = $derived([dp > 0 ? "Duel Points" : "", shards > 0 ? "crafting Shards" : ""].filter(Boolean).join(" and "));

  // Escape or Enter collects the gift.
  onEscape(() => onClose());
  createHotkey("Enter", () => onClose(), { conflictBehavior: "allow" });
</script>

<div class="mobile-dialog-host fixed inset-0 z-[60] grid place-items-center p-4" role="presentation">
  <button class="modal-scrim absolute inset-0 bg-void/52" transition:fade={{ duration: 200 }} onclick={onClose} aria-label="Collect"></button>

  <div
    class="mobile-full-dialog relative w-[min(460px,92vw)]"
    role="dialog"
    aria-modal="true"
    aria-label="Beta welcome"
    use:draggableDialog
    transition:scale={{ start: 0.94, opacity: 0, duration: 260, easing: expoOut }}
  >
    <DataWindow c={18} fill class="mobile-dialog-panel">
      <div data-dialog-drag-handle class="dialog-drag-handle shrink-0 px-6 pt-6 text-center phone:px-4 phone:pt-3">
        <div class="inline-flex items-center gap-2 chamfer-btn bg-gold/12 px-3 py-1" style="--c:5px">
          <span class="size-1.5 rounded-full bg-gold animate-pulse-soft" style="filter:drop-shadow(0 0 5px var(--color-gold))"></span>
          <span class="font-display text-[0.7rem] font-bold uppercase tracking-[0.26em] text-gold">Beta Exclusive</span>
        </div>
        <h2 class="mt-3 font-display text-2xl font-bold leading-tight text-ink">Welcome to the Beta!</h2>
        <p class="mt-1 text-sm text-ink-dim">Here's a stack of {gifts} to test everything the game has to offer.</p>
      </div>

      <!-- body (gift + disclosure) is the single scroll region; the Collect button stays pinned -->
      <div class="mobile-dialog-scroll max-h-[62dvh] min-h-0 flex-1 overflow-y-auto scroll-cyber">
        <!-- the headline gift(s) -->
        <div class="flex flex-wrap justify-center gap-3 px-6 py-6 text-center phone:gap-2 phone:py-3">
          {#if dp > 0}
            <div class="inline-flex items-baseline gap-2 chamfer-btn bg-gold/15 px-7 py-3 glow-cyan phone:px-4 phone:py-2" style="--c:9px" in:fly={{ y: 12, duration: 320, delay: 200 }}>
              <span class="font-display text-3xl font-bold text-gold tabular-nums phone:text-xl">+{dp.toLocaleString()}</span>
              <span class="font-display text-sm font-bold uppercase tracking-widest text-gold/80">DP</span>
            </div>
          {/if}
          {#if shards > 0}
            <div class="inline-flex items-baseline gap-2 chamfer-btn bg-cyan/15 px-7 py-3 glow-cyan phone:px-4 phone:py-2" style="--c:9px" in:fly={{ y: 12, duration: 320, delay: 280 }}>
              <span class="font-display text-3xl font-bold text-cyan tabular-nums phone:text-xl">+{shards.toLocaleString()}</span>
              <span class="font-display text-sm font-bold uppercase tracking-widest text-cyan/80">Shards</span>
            </div>
          {/if}
        </div>

        <!-- beta disclosure: play money, data wipe at launch, exclusive rewards for testers -->
        <div class="mx-6 mb-2 chamfer border border-gold/35 bg-gold/[0.06] p-4 text-left phone:mx-3 phone:p-3" style="--c:8px">
          <div class="flex items-center gap-2">
            <span aria-hidden="true">⚠️</span>
            <span class="font-display text-[0.72rem] font-bold uppercase tracking-[0.18em] text-gold">Beta Only, Please Read</span>
          </div>
          <ul class="mt-2 space-y-1.5 text-[0.82rem] leading-relaxed text-ink-dim phone:text-[0.72rem] phone:leading-snug">
            <li>This <strong class="text-ink">{gifts}</strong> gift is only handed out during the beta.</li>
            <li>All accounts and progress will be <strong class="text-ink">reset after official launch</strong>, nothing you earn now carries over.</li>
            <li>As a thank-you, beta testers will receive <strong class="text-gold">exclusive launch rewards</strong> for helping us test.</li>
          </ul>
        </div>
      </div>

      <div class="shrink-0 border-t border-cyan-dim/25 p-3 text-center phone:p-2">
        <button onclick={onClose} class="chamfer-btn bg-cyan/15 px-10 py-2.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-cyan transition-colors hover:glow-cyan" style="--c:8px">
          Collect
        </button>
      </div>
    </DataWindow>
  </div>
</div>
